import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BuilderDialog } from '@makyoapp/makyo-common'
import { AlignCenter16, AlignLeft16, AlignRight16 } from '@makyoapp/makyo-icon/icon'
import { Field, SingleIconSelect } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'
import { DeviceTab } from '@/components/DeviceTab'
import { TypographySelect } from '@/components/TypographySelect/TypographySelect'

import { SliderFieldSetting } from '../ComponentPanel/FieldSetting'

const iconAlignmentOptions = [
  {
    icon: <AlignLeft16 />,
    id: 'left',
    title: 'Start',
  },
  {
    icon: <AlignCenter16 />,
    id: 'center',
    title: 'Center',
  },
  {
    icon: <AlignRight16 />,
    id: 'right',
    title: 'End',
  },
]

interface DialogSettingDesignProps {
  dialog: BuilderDialog
  search: string
}

export const DialogSettingDesign = ({ dialog: { id: dialogId }, search }: DialogSettingDesignProps) => {
  const { dialogs, updateDialog, navbarSettings } = useAppBuilderContext()
  const [activeDevice, setActiveDevice] = useActiveDevice()

  const activeNavbar = navbarSettings[activeDevice]
  const device = activeNavbar.viewport as AppBreakpoint
  const devices = Object.keys(BUILDER_DEVICES) as AppBreakpoint[]

  const dialogIdx = useMemo(() => {
    return dialogs.findIndex((d) => d.id === dialogId)
  }, [dialogs, dialogId])

  const dialog = dialogs[dialogIdx]

  // debounce for width changes
  const widthTimeoutRef = useRef<NodeJS.Timeout>()

  // Get default width based on device
  const getDefaultWidth = (device: AppBreakpoint): string => {
    const defaults = {
      phone: '325px',
      'phone-landscape': '400px',
      tablet: '450px',
      'tablet-landscape': '500px',
      desktop: '530px',
      tv: '610px',
    }
    return defaults[device]
  }

  const [tempWidth, setTempWidth] = useState<string>(dialog.width?.[device] || getDefaultWidth(device))

  const debouncedUpdateWidth = useCallback(
    (newWidth: string) => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }

      widthTimeoutRef.current = setTimeout(() => {
        updateDialog(dialogIdx, (prev) => {
          // Preserve existing values and only update the current device
          const updatedWidth = {
            ...prev.width,
            [device]: newWidth,
          }

          // Ensure all breakpoints have values by using existing values or sensible defaults
          const defaultWidths = {
            phone: '325px',
            'phone-landscape': '400px',
            tablet: '450px',
            'tablet-landscape': '500px',
            desktop: '530px',
            tv: '610px',
          }

          prev.width = {
            ...defaultWidths,
            ...updatedWidth,
          }
        })
      }, 300) // 300ms delay
    },
    [updateDialog, dialogIdx, device]
  )

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }
    }
  }, [])

  // update tempWidth when device changes
  useEffect(() => {
    setTempWidth(dialog.width?.[device] || getDefaultWidth(device))
  }, [device, dialog.width])

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showLayout: true,
        showAlignment: true,
        showTypography: true,
        showColor: true,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showLayout: 'layout'.includes(searchLower) || 'width'.includes(searchLower),

      showAlignment:
        'icon alignment'.includes(searchLower) ||
        'title alignment'.includes(searchLower) ||
        'content alignment'.includes(searchLower) ||
        'footer alignment'.includes(searchLower),

      showTypography:
        'typography'.includes(searchLower) ||
        'typography headline'.includes(searchLower) ||
        'typography content'.includes(searchLower),

      showColor:
        'color'.includes(searchLower) ||
        'leading button color'.includes(searchLower) ||
        'trailing button color'.includes(searchLower),
    }
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['dialog-layout']}>
      {filteredItems.showLayout && (
        <AccordionItem itemId='dialog-layout' title={<Trans>Layout</Trans>}>
          <div className='flex overflow-hidden rounded-sm mb-4'>
            {devices.map((d) => (
              <DeviceTab key={d} device={d} isActive={device === d} onClick={() => setActiveDevice(d)} />
            ))}
          </div>
          <Field isVertical>
            <div className='flex items-center justify-between w-full'>
              <label>
                <Trans>Width</Trans>
              </label>
              <p className='text-xs text-light flex justify-end'>{dialog.width?.[device]}</p>
            </div>
            <SliderFieldSetting
              id='dialog-width'
              settingId='dialog-width'
              value={tempWidth || dialog.width?.[device]}
              sliderSettings={{
                max: dialog.width && dialog.width?.[device]?.startsWith('%') ? 100 : 1000,
                min: 10,
                unit: '%',
                showUnit: true,
              }}
              onChange={(value) => {
                const newWidth = value[0] ?? '350px'
                setTempWidth(newWidth)
                debouncedUpdateWidth(newWidth)
              }}
            />
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showAlignment && (
        <AccordionItem itemId='dialog-divider' title={<Trans>Alignment</Trans>}>
          <Field>
            <label>
              <Trans>Icon</Trans>
            </label>
            <SingleIconSelect
              id='dialog-icon-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.iconAlignment = value.id
                })
              }}
              selected={dialog.alignment?.iconAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Title</Trans>
            </label>
            <SingleIconSelect
              id='dialog-title-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.titleAlignment = value.id
                })
              }}
              selected={dialog.alignment?.titleAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Content</Trans>
            </label>
            <SingleIconSelect
              id='dialog-content-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.contentAlignment = value.id
                })
              }}
              selected={dialog.alignment?.contentAlignment ?? ''}
            />
          </Field>
          <Field>
            <label>
              <Trans>Footer</Trans>
            </label>
            <SingleIconSelect
              id='dialog-footer-alignment'
              options={iconAlignmentOptions}
              onClick={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.alignment.footerAlignment = value.id
                })
              }}
              selected={dialog.alignment?.footerAlignment ?? ''}
            />
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showTypography && (
        <AccordionItem itemId='dialog-design' title={<Trans>Typography</Trans>}>
          <Field isVertical>
            <label>Typography Headline</label>
            <TypographySelect
              value={{ typography: dialog.headlineTypography ?? 'Body Medium' } as any}
              // dialog.headlineTypography
              //   ? {  }
              //   : ({ typography: 'Body Medium' } as any)
              // }
              onChange={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.headlineTypography = value.typography
                })
              }}
            />
          </Field>
          <Field isVertical>
            <label>Typography Content</label>
            <TypographySelect
              value={{ typography: dialog.contentTypography ?? 'Body Medium' } as any}
              onChange={(value) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.contentTypography = value.typography
                })
              }}
            />
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showColor && (
        <AccordionItem itemId='dialog-colors' title={<Trans>Colors</Trans>}>
          <Field isVertical>
            <label>Leading Button</label>
            <BrandKitColorsDropdown
              value={dialog.leadingButtonColor || 'primary-50'}
              onChange={(colorVariant) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.leadingButtonColor = colorVariant
                })
              }}
              showExtendedColors={true}
            />
          </Field>
          <Field isVertical>
            <label>Trailing Button</label>
            <BrandKitColorsDropdown
              value={dialog.trailingButtonColor || 'neutral-100'}
              onChange={(colorVariant) => {
                updateDialog(dialogIdx, (prev) => {
                  prev.trailingButtonColor = colorVariant
                })
              }}
              showExtendedColors={true}
            />
          </Field>
        </AccordionItem>
      )}
    </Accordion>
  )
}
